import { useVirtualizer } from '@tanstack/react-virtual';
import { type RefObject, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { useContainerWidth } from '@/_shared/hooks/use-container-width';
import { cn } from '@/_shared/lib/utils';

import { fitColumns, useRemSize } from './cardGrid';

interface VirtualizedCardsProps<T> {
  items: readonly T[];
  renderItem: (item: T) => React.ReactNode;
  getItemKey: (item: T) => string;
  /** Target minimum card width (rem); the column count is derived from the measured content width. */
  minColumnRem?: number;
  /** Hard cap on columns regardless of width. */
  maxColumns?: number;
  /** Estimated card/row height (px) before measurement; real heights are measured after mount. */
  estimateRowHeight?: number;
  /** Max width (rem) of the centered content column. */
  maxContentRem?: number;
  /** Rendered in place of the grid when there are no items. */
  emptyState?: React.ReactNode;
  overscan?: number;
  /**
   * Virtualize against an *outer* scroller instead of owning one. Pass the page's scroll container when
   * the grid renders inline in a scrolling page (alongside other sections and a sticky toolbar) — the
   * grid then adds no scrollbar of its own, and the offset between the scroller's top and the grid is
   * measured and fed to the virtualizer as `scrollMargin` so windowing still lines up.
   */
  scrollElementRef?: RefObject<HTMLElement | null>;
  className?: string;
}

/**
 * A virtualized, responsive card grid. By default it owns its own scroll viewport (fill it with `h-full`
 * inside a `flex-1 min-h-0` parent); pass `scrollElementRef` to virtualize against a page scroller
 * instead. Either way it lays cards into rows of N columns derived from the measured content width and
 * virtualizes those rows, so a list of thousands only mounts the visible window. Row heights are
 * measured, so variable-height cards are fine. Purely presentational — filtering/sorting happen upstream.
 */
export function VirtualizedCards<T>({
  items,
  renderItem,
  getItemKey,
  minColumnRem = 20,
  maxColumns = 3,
  estimateRowHeight = 128,
  maxContentRem = 64,
  emptyState = null,
  overscan = 6,
  scrollElementRef,
  className,
}: VirtualizedCardsProps<T>): React.ReactElement {
  const ownScrollRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  // The rows' own origin. `contentRef` carries `py-6`, so measuring from it put the virtualizer's origin
  // 24px above the first card.
  const rowsRef = useRef<HTMLDivElement>(null);
  const width = useContainerWidth(contentRef);
  const external = scrollElementRef != null;

  const rem = useRemSize();
  const columns = fitColumns(width, minColumnRem, maxColumns, rem);
  const rowCount = Math.ceil(items.length / columns);

  // Only the EXTERNAL ref needs resolving through state: it belongs to an ancestor, and React attaches a
  // parent's ref after its children's layout effects run, so at mount it is still null and the virtualizer
  // would measure a zero viewport. Our OWN ref is read live — snapshotting it would make the lookup
  // one-shot and unable to recover if the viewport ever remounts.
  const [externalScrollEl, setExternalScrollEl] = useState<HTMLElement | null>(null);
  useEffect(() => {
    if (!external) return;
    setExternalScrollEl(scrollElementRef?.current ?? null);
  }, [external, scrollElementRef]);
  const getScrollEl = (): HTMLElement | null => (external ? externalScrollEl : ownScrollRef.current);

  // How far the grid sits below the top of an external scroller's content — everything above it (page
  // sections, the sticky toolbar) that the virtualizer must offset its window by. Measured from live
  // rects rather than `offsetTop`, which is relative to the offset parent and not necessarily the
  // scroller. Re-measured when the scroller resizes or the item set changes (sections settling above).
  const [scrollMargin, setScrollMargin] = useState(0);
  useLayoutEffect(() => {
    const content = rowsRef.current;
    if (!external || !externalScrollEl || !content) return;
    const measure = (): void => {
      setScrollMargin(
        content.getBoundingClientRect().top - externalScrollEl.getBoundingClientRect().top + externalScrollEl.scrollTop,
      );
    };
    measure();
    // Watch the scroller AND its direct children: what shifts the grid is content ABOVE it (page sections,
    // the toolbar), which changes a child's height without touching the scroller's own box.
    const resizeObserver = new ResizeObserver(measure);
    resizeObserver.observe(externalScrollEl);
    for (const child of externalScrollEl.children) resizeObserver.observe(child);
    // Sections above the grid can also mount/unmount outright — a childList change no existing box
    // resizes for, so the ResizeObserver never fires. Re-measure and start observing the new children.
    const mutationObserver = new MutationObserver(() => {
      measure();
      for (const child of externalScrollEl.children) resizeObserver.observe(child);
    });
    mutationObserver.observe(externalScrollEl, { childList: true });
    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [external, externalScrollEl, items.length]);

  // Snap back to the top when the item set changes (filter / search / sort) — otherwise the retained
  // scroll offset can leave the viewport on the wrong slice of the new results (or momentarily blank).
  // Only when we own the scroller: yanking the *page* to the top would rip the user past the sections
  // above on every keystroke, and a shrinking list clamps the page scroll on its own anyway.
  useLayoutEffect(() => {
    if (!external && ownScrollRef.current) ownScrollRef.current.scrollTop = 0;
  }, [items, external]);

  // eslint-disable-next-line react-hooks/incompatible-library -- TanStack Virtual returns a mutable instance React Compiler can't memoize; it skips this component safely (TanStack memoizes internally).
  const virtualizer = useVirtualizer({
    count: rowCount,
    getScrollElement: getScrollEl,
    estimateSize: () => estimateRowHeight,
    overscan,
    scrollMargin,
    // Key each virtual row by its first item, not its slot index, so measured heights follow content
    // across a re-sort / filter / column-count change instead of being reused for whatever now sits there.
    getItemKey: (index) => {
      const first = items[index * columns];
      return first ? getItemKey(first) : `row-${index}`;
    },
  });

  return (
    <div
      ref={external ? undefined : ownScrollRef}
      className={cn(!external && 'scrollbar-thin overflow-y-auto', className)}
    >
      <div ref={contentRef} className="mx-auto w-full px-6 py-6" style={{ maxWidth: `${maxContentRem}rem` }}>
        {items.length === 0 ? (
          emptyState
        ) : (
          <div ref={rowsRef} className="relative w-full" style={{ height: virtualizer.getTotalSize() }}>
            {virtualizer.getVirtualItems().map((virtualRow) => {
              const start = virtualRow.index * columns;
              const rowItems = items.slice(start, start + columns);
              return (
                <div
                  key={virtualRow.key}
                  data-index={virtualRow.index}
                  ref={virtualizer.measureElement}
                  className="absolute top-0 left-0 w-full"
                  // `start` is measured from the scroller's origin, so subtract the grid's own offset
                  // within it to get a position relative to this container (a no-op when scrollMargin is 0).
                  style={{ transform: `translateY(${virtualRow.start - virtualizer.options.scrollMargin}px)` }}
                >
                  <div
                    className="grid gap-4 pb-4"
                    style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
                  >
                    {rowItems.map((item) => (
                      <div key={getItemKey(item)} className="min-w-0">
                        {renderItem(item)}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
